import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/api-clients/react-query';
import { privateApi } from '@/lib/api-clients/rest-client';
import { AuthParams, useAccountStore } from '@/store/use-account-store';
import { OrderStatus, TransferType } from '@/types/enums';
interface GetTransfersParams {
  id?: string;
  orderType?: string;
  startTime?: number;
  endTime?: number;
  limit?: number;
}

export type NewTransferResponse = {
  id: string;
  size: string;
  orderType: TransferType;
  status: OrderStatus.NEW;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

export type FilledTransferResponse = {
  id: string;
  size: string;
  orderType: TransferType;
  status: OrderStatus.FILLED;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

export type RejectedTransferResponse = {
  id: string;
  orderType: TransferType;
  status: OrderStatus.REJECTED;
  code: number;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

export type VerifiedTransferResponse = {
  id: string;
  orderType: TransferType;
  status: OrderStatus.VERIFIED;
  size: string;
  chainId: number;
  postTime: number;
  requestArgs: string;
  signer: string;
  signature: string;
  signatureProof: string;
  validatorSignature: string;
  isSolanaNative: boolean;
};

export type TransferResponse =
  | NewTransferResponse
  | FilledTransferResponse
  | RejectedTransferResponse
  | VerifiedTransferResponse;

export const getTransfers = async ({
  id,
  orderType,
  startTime,
  endTime,
  limit,
}: GetTransfersParams): Promise<TransferResponse[]> => {
  return privateApi.get('/transfer', {
    params: {
      id,
      orderType,
      startTime,
      endTime,
      limit,
    },
  });
  // .then((res) => {
  //   console.error(res, res.length);
  //   res.forEach(handleTransferForStore);
  //   return res;
  // });
};

export const getTransfersQueryOptions = (
  params: GetTransfersParams,
  authParams: AuthParams | undefined,
) => {
  const {
    endTime = useAccountStore.getState().getAccurateTime(),
    limit = 1000,
    ...restOfParams
  } = params;

  const paramsToCache = { ...restOfParams };
  return queryOptions({
    queryKey: ['transfers', paramsToCache, authParams?.signingKey],
    queryFn: () => getTransfers(params),
  });
};

type UseTransfersOptions = GetTransfersParams & {
  queryConfig?: QueryConfig<typeof getTransfersQueryOptions>;
};

export const useTransfers = ({
  queryConfig,
  ...params
}: UseTransfersOptions = {}) => {
  const { hasAuth, authParams } = useAccountStore((state) => ({
    hasAuth: state.hasAuth,
    authParams: state.getAuthParams(),
  }));

  const result = useQuery({
    enabled: hasAuth && !!authParams?.signingKey,
    ...getTransfersQueryOptions(params, authParams),
    ...queryConfig,
  });

  return result;
};
